import { DeleteOutlined } from '@ant-design/icons';
import { Popconfirm, Row } from 'antd';
import React from 'react';
import { Props } from '.';
import CapSelector from '../../../../components/cap-selector/CapSelector';
import Icon from '../../../../components/icon/Icon';
import ShotLocation from '../../../../components/shot-location/ShotLocation';
import { CapNumber } from '../../../../core/models/CapNumber';
import { getFieldPlayIneligibleCapNumbers, getTeamId } from '../../../../helpers/EventHelper';
import {
    CreateMatchPlayerCommandOfMatchPlayer,
    EventVm,
    ExclusionRequest,
    ExclusionTypeEnum,
    UpdateEventCommand,
} from '../../../../utils/api';

class Exclusion extends React.PureComponent<Props> {
    /** If double exclusion is on, assisted-by must never be set (stale data from before toggle or API). */
    public componentDidMount(): void {
        this.clearAssistedWhenDouble();
    }

    public componentDidUpdate(): void {
        this.clearAssistedWhenDouble();
    }

    private clearAssistedWhenDouble(): void {
        const { event, updateEvent } = this.props;
        const ex = event.exclusion;
        if (ex?.isDoubleExclusion && ex.assistedById != null) {
            const eventVm = event as EventVm;
            const updatedEvent = new UpdateEventCommand({
                ...event,
                exclusion: new ExclusionRequest({
                    ...ex,
                    assistedById: undefined,
                    id: eventVm.exclusionId || 0,
                }),
            });
            updatedEvent.id = event.id;
            updateEvent(updatedEvent);
        }
    }

    public handleFouledPlayerSelect = (cap: number): void => {
        const { event, updateEvent, lineups } = this.props;

        const eventVm = event as EventVm;

        const otherTeamLineups = lineups.filter(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.teamId !== getTeamId(event)
        );
        const lineup = otherTeamLineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.number === cap
        );
        const playerId = lineup?.id;

        const prevAssisted = event.exclusion?.assistedById;
        const isDouble = event.exclusion?.isDoubleExclusion;
        const assistedStays =
            !isDouble &&
            prevAssisted != null &&
            prevAssisted !== playerId &&
            otherTeamLineups.some(
                (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.id === prevAssisted
            );

        const updatedEvent = new UpdateEventCommand({
            ...event,
            exclusion: new ExclusionRequest({
                ...event.exclusion,
                fouledPlayerId: playerId,
                assistedById: assistedStays ? prevAssisted : undefined,
                id: eventVm.exclusionId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleFouledPlayerClear = (): void => {
        const { event, updateEvent } = this.props;

        const eventVm = event as EventVm;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            exclusion: new ExclusionRequest({
                ...event.exclusion,
                fouledPlayerId: undefined,
                assistedById: undefined,
                isDoubleExclusion: false,
                id: eventVm.exclusionId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleAssistedByPlayerSelect = (cap: number): void => {
        const { event, updateEvent, lineups } = this.props;

        const eventVm = event as EventVm;

        // Same bench as fouled player (victim's team), not the fouler's team (getTeamId event side).
        const otherTeamLineups = lineups.filter(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.teamId !== getTeamId(event)
        );
        const lineup = otherTeamLineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.number === cap
        );
        const playerId = lineup?.id;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            exclusion: new ExclusionRequest({
                ...event.exclusion,
                assistedById: playerId,
                id: eventVm.exclusionId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleAssistedByPlayerClear = (): void => {
        const { event, updateEvent } = this.props;

        const eventVm = event as EventVm;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            exclusion: new ExclusionRequest({
                ...event.exclusion,
                assistedById: undefined,
                id: eventVm.exclusionId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleSuspensionTypeSelect = (exclusionType: ExclusionTypeEnum): void => {
        const { event, updateEvent } = this.props;

        const eventVm = event as EventVm;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            exclusion: new ExclusionRequest({
                ...event.exclusion,
                typeId: exclusionType,
                isPenaltyExclusion:
                    exclusionType === ExclusionTypeEnum.Penalty
                        ? event.exclusion.isPenaltyExclusion
                        : false,
                id: eventVm.exclusionId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public toggleDoubleExclusion = (checked: boolean): void => {
        const { event, updateEvent } = this.props;

        const eventVm = event as EventVm;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            exclusion: new ExclusionRequest({
                ...event.exclusion,
                isDoubleExclusion: checked,
                assistedById: checked ? undefined : event.exclusion?.assistedById,
                id: eventVm.exclusionId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public togglePenaltyExclusion = (checked: boolean): void => {
        const { event, updateEvent } = this.props;

        const eventVm = event as EventVm;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            exclusion: new ExclusionRequest({
                ...event.exclusion,
                isPenaltyExclusion: checked,
                id: eventVm.exclusionId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public handleExclusionLocationSelect = (x: number, y: number): void => {
        const { event, updateEvent } = this.props;

        const eventVm = event as EventVm;

        const updatedEvent = new UpdateEventCommand({
            ...event,
            exclusion: new ExclusionRequest({
                ...event.exclusion,
                locationX: x,
                locationY: y,
                id: eventVm.exclusionId || 0,
            }),
        });

        updatedEvent.id = event.id;

        updateEvent(updatedEvent);
    };

    public render = (): React.ReactElement => {
        const { event, lineups, isHomeTeam, clearEvent, deleteEvent, events } = this.props;
        const eventVm = event as EventVm;
        const eventExists = !!eventVm.id;
        const exclusionType = event.exclusion?.typeId;
        const otherTeamCapClass = `c-shot-form__cap-selector c-shot-form__cap-selector--${
            isHomeTeam ? 'away' : 'home'
        }`;
        const isDoubleExclusion = event.exclusion?.isDoubleExclusion;
        const isPenaltyExclusion = event.exclusion?.isPenaltyExclusion;

        const fouledPlayerId = event.exclusion?.fouledPlayerId;
        const fouledPlayerLineup = lineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.id === fouledPlayerId
        );
        const assistedById = event.exclusion?.assistedById;
        const assistedByPlayerLineup = lineups.find(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.id === assistedById
        );
        const otherTeamLineups = lineups.filter(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.teamId !== getTeamId(event)
        );
        const assistedByCapOptions = otherTeamLineups.filter(
            (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.id !== fouledPlayerId
        );

        const capSelectorDisabledForOtherTeamCaps = getFieldPlayIneligibleCapNumbers(
            events,
            otherTeamLineups
        );

        const exclusionLocationX = event.exclusion?.locationX;
        const exclusionLocationY = event.exclusion?.locationY;

        const pill = (
            label: string,
            active: boolean,
            onClick: () => void,
            disabled?: boolean
        ): React.ReactElement => (
            <span
                className={`c-shot-pill${active ? ' c-shot-pill--active' : ''}${
                    disabled ? ' c-shot-pill--disabled' : ''
                }`}
                onClick={disabled ? undefined : onClick}
                role="button"
            >
                {label}
            </span>
        );

        return (
            <div className="event-create c-exclusion-form">
                <div className="c-exclusion-form__main">
                    <div className="c-exclusion-form__pills">
                        {pill('Penalty', exclusionType === ExclusionTypeEnum.Penalty, () =>
                            exclusionType === ExclusionTypeEnum.Penalty
                                ? this.handleSuspensionTypeSelect(ExclusionTypeEnum.Exclusion)
                                : this.handleSuspensionTypeSelect(ExclusionTypeEnum.Penalty)
                        )}
                        {pill('Exc with sub', exclusionType === ExclusionTypeEnum.Suspension, () =>
                            exclusionType === ExclusionTypeEnum.Suspension
                                ? this.handleSuspensionTypeSelect(ExclusionTypeEnum.Exclusion)
                                : this.handleSuspensionTypeSelect(ExclusionTypeEnum.Suspension)
                        )}
                        {pill('Violent action', exclusionType === ExclusionTypeEnum.Brutality, () =>
                            exclusionType === ExclusionTypeEnum.Brutality
                                ? this.handleSuspensionTypeSelect(ExclusionTypeEnum.Exclusion)
                                : this.handleSuspensionTypeSelect(ExclusionTypeEnum.Brutality)
                        )}
                        {pill(
                            'Penalty Excl.',
                            !!isPenaltyExclusion,
                            () => this.togglePenaltyExclusion(!isPenaltyExclusion),
                            exclusionType !== ExclusionTypeEnum.Penalty
                        )}
                        {pill(
                            'Double Excl.',
                            !!isDoubleExclusion,
                            () => this.toggleDoubleExclusion(!isDoubleExclusion),
                            !fouledPlayerLineup
                        )}
                    </div>
                    <div className="c-exclusion-form__caps-stack">
                        <div className="c-exclusion-form__cap-module">
                            <div className="c-shot-form__caps-label">Fouled player</div>
                            <CapSelector
                                capClass={otherTeamCapClass}
                                onSelect={this.handleFouledPlayerSelect}
                                onClear={this.handleFouledPlayerClear}
                                caps={otherTeamLineups.map(
                                    (l: CreateMatchPlayerCommandOfMatchPlayer): CapNumber => ({
                                        cap: l.number,
                                        capNumber: l.capNumber || l.number,
                                    })
                                )}
                                activeCap={fouledPlayerLineup ? fouledPlayerLineup.number : null}
                                activeCapNumber={
                                    fouledPlayerLineup
                                        ? fouledPlayerLineup.capNumber || fouledPlayerLineup.number
                                        : null
                                }
                                disabledCaps={capSelectorDisabledForOtherTeamCaps}
                            />
                        </div>
                        {!isDoubleExclusion && (
                            <div className="c-exclusion-form__cap-module">
                                <div className="c-shot-form__caps-label">Pass made by</div>
                                <CapSelector
                                    capClass={otherTeamCapClass}
                                    onSelect={this.handleAssistedByPlayerSelect}
                                    onClear={this.handleAssistedByPlayerClear}
                                    caps={assistedByCapOptions.map(
                                        (l: CreateMatchPlayerCommandOfMatchPlayer): CapNumber => ({
                                            cap: l.number,
                                            capNumber: l.capNumber || l.number,
                                        })
                                    )}
                                    activeCap={
                                        assistedByPlayerLineup
                                            ? assistedByPlayerLineup.number
                                            : null
                                    }
                                    activeCapNumber={
                                        assistedByPlayerLineup
                                            ? assistedByPlayerLineup.capNumber ||
                                              assistedByPlayerLineup.number
                                            : null
                                    }
                                    disabledCaps={capSelectorDisabledForOtherTeamCaps}
                                />
                            </div>
                        )}
                    </div>
                    <div className="c-shot-form__actions">
                        <span
                            className="c-shot-form__action-btn"
                            onClick={(): void => clearEvent()}
                            role="button"
                            title="Close"
                        >
                            <Icon name="icon-times" />
                        </span>
                        {eventExists && (
                            <Popconfirm
                                title="Are you sure you want to delete this event?"
                                onConfirm={(): void => deleteEvent(eventVm)}
                                okText="Delete"
                                okButtonProps={{ danger: true }}
                                cancelText="Cancel"
                            >
                                <span
                                    className="c-shot-form__action-btn c-shot-form__action-btn--delete"
                                    role="button"
                                    title="Delete"
                                >
                                    <DeleteOutlined />
                                </span>
                            </Popconfirm>
                        )}
                    </div>
                </div>
                <div className="c-exclusion-form__location">
                    <Row>
                        <ShotLocation
                            x={exclusionLocationX}
                            y={exclusionLocationY}
                            onSelect={this.handleExclusionLocationSelect}
                        />
                    </Row>
                </div>
            </div>
        );
    };
}

export default Exclusion;
